import {
  WithBase,
  WithID,
  WithRef,
} from "@converge-collective/common/models/Base";
import { Challenge } from "@converge-collective/common/models/Challenge";
import { NetworkMembership } from "@converge-collective/common/models/Network";
import {
  Profile,
  ProfilePreferences,
  ProfilePrivateAttributes,
} from "@converge-collective/common/models/Profile";
import {
  firestoreData,
  modelFromSnap,
  shallowTSToDates,
} from "@converge-collective/common/util";
import { useEffect, useState } from "react";
import { useFirestore, useFirestoreCollectionData } from "reactfire";

// TODO - consider hook creators that accept:
// 1. params object
// 2. path in firestore (template string that can access params)
// and returns [state, setState] array similar to useState.
// it's basically useState where firestore is the state store.

export default function useProfile(
  userId?: string
): [
  WithRef<Profile> | null,
  (p: Partial<Profile>, merge?: boolean) => Promise<void>
] {
  const [profile, setProfile] = useState<WithRef<Profile> | null>(null);
  const firestore = useFirestore();
  const FieldValue = useFirestore.FieldValue;
  // console.log("useProfile", { userId });
  // listen to updates
  useEffect(() => {
    if (userId) {
      const profileRef = firestore.collection("profiles").doc(userId);
      // onSnapshot can return partial results, so we need to populate the
      // profile the first time
      const unsub = profileRef.onSnapshot((p) => {
        // when profile is retrieved from cache it'll contain pending writes.
        // when the user first loads the app we update `timestamp`, so the
        // incomplete profile will only include `timestamp` and `id`, unless we
        // ignore it when fromCache is true.
        // so: avoid setting the profile until it's complete

        if (!p.metadata.fromCache) {
          const profile = {
            ...p.data(),
            id: p.id,
            ref: profileRef,
          } as WithRef<Profile>;
          // console.debug("useProfile got update", {
          //   metadata: p.metadata,
          //   profile,
          // });
          setProfile(shallowTSToDates(profile));
        }
      });
      return () => unsub();
    }
  }, [firestore, userId]);
  return [
    profile,
    async (p: Partial<Profile>, merge = true) => {
      console.log("setProfile", { profile: p });
      // TODO - this should be how every `set/create/update` is handled where
      // `undefined` is treated as FieldValue.delete()
      const undefToFieldValue = Object.fromEntries(
        Object.entries(p).map(([k, v]) => [
          k,
          v === undefined ? FieldValue.delete() : v,
        ])
      );
      return firestore
        .collection("profiles")
        .doc(userId)
        .set(undefToFieldValue, { merge });
    },
  ];
}

// TODO consider a fn that can generate hooks like these
export function usePrivateAttrs(
  userId?: string
): [
  WithBase<ProfilePrivateAttributes> | null,
  (p: ProfilePrivateAttributes) => Promise<void>
] {
  const [data, setData] = useState<WithBase<ProfilePrivateAttributes> | null>(
    null
  );
  const firestore = useFirestore();
  console.log("useProfile", { userId });
  // listen to updates
  useEffect(() => {
    if (userId) {
      const profileRef = firestore
        .collection("profiles")
        .doc(userId)
        .collection("private")
        .doc("attributes");
      const unsub = profileRef.onSnapshot((p) => {
        const prefs = {
          ...p.data(),
          id: p.id,
        } as WithBase<ProfilePrivateAttributes>;
        setData(prefs);
      });
      return () => unsub();
    }
  }, [firestore, userId]);
  return [
    data,
    async (p: ProfilePrivateAttributes) => {
      return firestore
        .collection("profiles")
        .doc(userId)
        .collection("private")
        .doc("attributes")
        .set(p, { merge: true }); // use set incase the doc doesn't exist yet
    },
  ];
}

// return network memberships that the user is a member of
export function useNetworksMemberships(
  userId?: string
): [NetworkMembership[] | undefined] {
  const firestore = useFirestore();
  // const [data, setData] = useState<NetworkMembership>();
  const profileRef = firestore.collection("profiles").doc(userId);
  const query = firestore
    .collectionGroup("members")
    .where("profileRef", "==", profileRef);

  const { data } = useFirestoreCollectionData<NetworkMembership>(query, {
    idField: "id",
  });
  return [data];
}

export function usePreferences(
  userId?: string
): [
  ProfilePreferences | null,
  (p: ProfilePreferences) => Promise<void>,
  boolean
] {
  const [data, setData] = useState<ProfilePreferences | null>(null);
  const firestore = useFirestore();
  const [isLoaded, setIsLoaded] = useState(false);
  console.log("useProfile", { userId });
  // listen to updates
  useEffect(() => {
    if (userId) {
      const profileRef = firestore
        .collection("profiles")
        .doc(userId)
        .collection("private")
        .doc("preferences");
      const unsub = profileRef.onSnapshot((p) => {
        const prefs = { ...p.data(), id: p.id } as ProfilePreferences;
        setIsLoaded(true);
        setData(prefs);
      });
      return () => unsub();
    }
  }, [firestore, userId]);
  return [
    data,
    async (p: Partial<ProfilePreferences>) => {
      return (
        firestore
          .collection("profiles")
          .doc(userId)
          .collection("private")
          .doc("preferences")
          // use set incase the doc doesn't exist yet
          .set(p, { merge: true })
      );
    },
    isLoaded,
  ];
}

export function useCurrentChallenges(
  userId: string
): WithID<Challenge>[] | null {
  const [challenges, setChallenges] = useState<WithID<Challenge>[] | null>(
    null
  );
  const [profile] = useProfile(userId);
  const firestore = useFirestore();

  useEffect(() => {
    const challengesJoined = profile?.challengesJoined;
    const fetchChallenges = async () => {
      console.log("fetch challenges");
      const challengeData = (
        await Promise.all(
          (challengesJoined as string[]).map(async (challengePath: string) => {
            const challengeRef = firestore.doc(challengePath);
            const data = await challengeRef.get();
            return modelFromSnap<Challenge>(data);
          })
        )
      )
        // filter out challenges that only have an ID and ref - this happens when a
        // challenge that the user previously joined was deleted
        .filter((challenge) => Object.keys(challenge).length > 2);
      setChallenges(challengeData);
    };
    if (challengesJoined) fetchChallenges();
  }, [firestore, profile]);

  return challenges;
}
